/**
 * Example page demonstrating the faceted filtering system with Projects
 */

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { ProjectDetails } from './components/ProjectDetails';
import { ProjectTable } from './components/ProjectTable';
import { useColumns } from './components/ProjectTable/hooks/useColumns';
import { PROJECT_ID_PARAMETER_NAME } from './constants';
import {
  defaultTableColumnVisibility,
  defaultCardFieldsVisibility,
  PORTFOLIO_SORT_FIELD_MAP,
  PORTFOLIO_STORAGE_KEYS,
  projectsConfig,
  columnVisibilityRequirements,
} from './portfolio.config';
import styles from './Portfolio.module.scss';
import { useProjectsFilterViewStore } from './useProjectsFilterViewStore';
import { useGetConfigurationFeatures } from '@/api/REST/configuration-features';
import { Project } from '@/api/typesense/projects';
import { FilterBar } from '@/components/filters';
import { Views } from '@/components/filters/Views';
import { PageTitle } from '@/components/global/PageTitle';
import { Panels } from '@/components/layouts/Panels';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { PageHeader } from '@/components/ui/PageHeader';
import { ViewOptionsPopover } from '@/components/ui/ViewOptionsPopover';
import { areaIcons } from '@/constants/areaIcons';
import { uiIcons } from '@/constants/uiIcons';
import { useHasNewProjectManagement } from '@/hooks/featureFlags';
import { useSearchResults, useFilterManager } from '@/hooks/filters';
import {
  useOrderedColumns,
  useViewMode,
  useTablePreferences,
  useFilteredColumns,
} from '@/hooks/grid';
import { usePanelDrawer, usePopover, useSearchInput } from '@/hooks/ui';
import { useHasPermissionToAccessPage } from '@/hooks/useHasPermissionToAccessPage';
import type { FilterClause } from '@/lib/filter-dsl';
import { useNewProjectModalStore } from '@/stores/newProjectModal';

export const Portfolio = () => {
  const { t } = useTranslation('portfolio');
  const { hasPermission: hasNewProjectPermission } =
    useHasPermissionToAccessPage('NewProject');
  const openNewProjectModal = useNewProjectModalStore((state) => state.open);
  const hasNewProjectManagement = useHasNewProjectManagement();
  const [searchParams] = useSearchParams();
  const selectedProjectId = searchParams.get(PROJECT_ID_PARAMETER_NAME);
  const { searchInput, setSearchInput, debouncedSearchInput } =
    useSearchInput();

  const {
    isPanelOpen,
    expandDrawer,
    drawerRef,
    openPanel,
    openPanelAndFocus,
    closePanel,
    handleEscapeFromDrawer,
  } = usePanelDrawer({ selectedId: selectedProjectId });

  // Get filter manager (manages both filters and views)
  const {
    filterSet,
    removeFilter,
    updateFilter,
    filterViews,
    currentFilters: _currentFilters,
    currentViewId,
    currentView,
    filterPanelOpen,
    selectView,
    saveCurrentView,
    saveAsNewView,
    createEmptyView,
    resetFilters,
    clearViewAndFilterSelection,
    toggleFilterPanel,
    hasUnsavedChanges,
    hasFilters,
    isSaving,
    isTransitioning,
    isCreatingView,
    isSavingAsNewView,
    startCreatingView,
    endCreatingView,
    startSavingAsNewView,
    endSavingAsNewView,
    duplicateView,
    createViewMutation,
    updateViewMutation,
    deleteViewMutation,
  } = useFilterManager({
    useStore: useProjectsFilterViewStore,
  });

  // View mode state (kept for when cards view is ready)
  const { viewMode, setViewMode: _setViewMode } = useViewMode(
    PORTFOLIO_STORAGE_KEYS.viewMode,
  );

  // Table and cards view preferences
  const {
    tableSortModel,
    cardsSortModel,
    tableColumnVisibility,
    cardFieldsVisibility,
    handleTableColumnToggle,
    handleCardFieldsToggle,
    columnWidths,
    columnOrder,
    density,
    setDensity,
    setTableColumnVisibility,
    handleColumnWidthChange,
    handleColumnOrderChange,
    setTableSortModel,
  } = useTablePreferences(PORTFOLIO_STORAGE_KEYS, {
    tableColumnVisibility: defaultTableColumnVisibility,
    tableSort: [{ field: 'Name', sort: 'asc' }],
    cardFieldsVisibility: defaultCardFieldsVisibility,
    cardsSort: [{ field: 'Name', sort: 'asc' }],
  });

  // Active state based on view mode
  const activeSortModel =
    viewMode === 'table' ? tableSortModel : cardsSortModel;
  const activeColumnVisibility =
    viewMode === 'table' ? tableColumnVisibility : cardFieldsVisibility;
  const activeHandleColumnToggle =
    viewMode === 'table' ? handleTableColumnToggle : handleCardFieldsToggle;

  // Map sort field config from PORTFOLIO_SORT_FIELD_MAP
  const sortFieldConfig = Object.entries(PORTFOLIO_SORT_FIELD_MAP).map(
    ([field, sortField]) => ({ field, sortField }),
  );

  // Fetch search results with infinite scroll
  const { data, isLoading, hasNextPage, fetchNextPage } =
    useSearchResults<Project>({
      config: projectsConfig,
      filterSet,
      sortModel: activeSortModel,
      sortFieldMappings: sortFieldConfig, // Pass sort field mappings from config
      query: debouncedSearchInput, // Debounced search query (200ms delay)
    });

  const allProjects = data?.pages?.flatMap((page) => page.documents) || [];
  const totalResults = data?.pages?.[0]?.totalResults || 0;

  const handleAddFilter = (clause: FilterClause) => {
    updateFilter(clause);
  };

  // View options popover state
  const viewOptions = usePopover({ id: 'portfolio-view-options' });

  // Fetch features and settings for column visibility
  const { data: features, isLoading: isFeaturesLoading } =
    useGetConfigurationFeatures();

  // Get all columns with width preferences
  const allColumns = useColumns(columnWidths);

  // Filter columns based on features and settings
  const filteredColumns = useFilteredColumns({
    columns: allColumns,
    features,
    requirements: columnVisibilityRequirements,
  });

  // Reorder columns based on saved order
  const columns = useOrderedColumns(filteredColumns, columnOrder);

  return (
    <>
      <PageTitle title={t('PageTitle')} />
      <Panels
        autoSaveId="projects-portfolio-panels"
        expandDrawer={expandDrawer}
        defaultLayoutRatio="2:1"
        collapseDrawerOnEscape
        onDrawerCollapse={closePanel}
        drawerRef={drawerRef}
        onEscapeFromDrawer={handleEscapeFromDrawer}
      >
        <Panels.Main
          panelContentProps={{
            hideOverflow: viewMode === 'table',
            hasPaddingBottom: viewMode === 'cards',
          }}
        >
          <PageHeader title={t('PageTitle')} iconName={areaIcons.projects}>
            {hasNewProjectPermission && (
              <PageHeader.Info>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Icon name={uiIcons.actions.add} />}
                  {...(hasNewProjectManagement
                    ? { onClick: openNewProjectModal }
                    : {
                        component: Link,
                        to: '/ProjectManagement/Project/New',
                      })}
                  data-automation-id="portfolio-new-project-button"
                >
                  {t('NewProjectButton')}
                </Button>
              </PageHeader.Info>
            )}
          </PageHeader>

          {/* Search and Controls */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="start"
            gap={2}
          >
            {/* View tabs - shows saved views */}
            <Views
              filterViews={filterViews}
              createView={createViewMutation}
              createEmptyView={createEmptyView}
              saveAsNewView={saveAsNewView}
              duplicateView={duplicateView}
              updateView={updateViewMutation}
              deleteViewMutation={deleteViewMutation}
              currentViewId={currentViewId}
              hasUnsavedChanges={hasUnsavedChanges}
              onViewSelect={(view) => {
                setSearchInput('');
                if (view?.filterViewId) {
                  // Pass filterParameters to handle newly created views
                  selectView(view.filterViewId, view.filterParameters);
                } else {
                  clearViewAndFilterSelection();
                }
              }}
              isCreatingView={isCreatingView}
              isSavingAsNewView={isSavingAsNewView}
              onCreateStart={startCreatingView}
              onCreateEnd={endCreatingView}
              onSaveAsNewEnd={endSavingAsNewView}
              filterPanelOpen={filterPanelOpen}
              onToggleFilterPanel={toggleFilterPanel}
            />

            <Stack direction="row" alignItems="center" gap={1}>
              <FormControl>
                {/* This div is needed to show the placeholder */}
                <div>
                  <InputLabel visuallyHidden htmlFor="search-input">
                    {t('SearchLabel', { ns: 'ui' })}
                  </InputLabel>
                </div>
                <OutlinedInput
                  id="search-input"
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={t('SearchLabel', { ns: 'ui' })}
                  pilled
                  size="small"
                  data-automation-id="portfolio-search-input"
                  className={styles.SearchInput}
                />
              </FormControl>

              <IconButton
                size="small"
                color="secondary"
                tooltip={t('tooltip.ViewOptions')}
                iconName={uiIcons.controls.columnPicker}
                variant="outlined"
                onClick={viewOptions.handleOpen}
                aria-haspopup="dialog"
                aria-expanded={viewOptions.open ? 'true' : undefined}
                aria-controls={viewOptions.open ? viewOptions.id : undefined}
                data-automation-id="portfolio-view-options-button"
              />

              <ViewOptionsPopover
                id={viewOptions.id}
                open={viewOptions.open}
                anchorEl={viewOptions.anchorEl}
                onClose={viewOptions.handleClose}
                columns={columns}
                columnVisibilityModel={activeColumnVisibility}
                onColumnToggle={activeHandleColumnToggle}
                density={density}
                onDensityChange={setDensity}
              />
              {/* View mode toggle - Hidden until cards view is ready */}
              {/* <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_event, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode);
                  }
                }}
                size="small"
                aria-label="view mode"
              >
                <ToggleButton
                  value="table"
                  tooltip="Table view"
                  iconName={uiIcons.controls.tableView}
                  data-automation-id="portfolio-table-view-button"
                />
                <ToggleButton
                  value="cards"
                  tooltip="Cards view"
                  iconName={uiIcons.controls.gridView}
                  data-automation-id="portfolio-cards-view-button"
                />
              </ToggleButtonGroup> */}
            </Stack>
          </Stack>

          <FilterBar
            id="filter-panel"
            aria-expanded={filterPanelOpen}
            hidden={!filterPanelOpen}
            config={projectsConfig}
            filterSet={filterSet}
            searchQuery={debouncedSearchInput}
            onAddFilter={handleAddFilter}
            onRemoveFilter={removeFilter}
            onUpdateFilter={updateFilter}
            currentView={currentView}
            hasUnsavedChanges={hasUnsavedChanges}
            hasFilters={hasFilters}
            isSaving={isSaving}
            isTransitioning={isTransitioning}
            onSaveCurrentView={saveCurrentView}
            onSaveAsNewView={startSavingAsNewView}
            onResetFilters={resetFilters}
          />

          <Box sx={{ flex: 1, minHeight: 0 }}>
            {/* Always show table view for now - cards view coming soon */}
            <ProjectTable
              items={allProjects}
              isLastPage={!hasNextPage}
              showMore={fetchNextPage}
              columns={columns}
              columnVisibilityModel={tableColumnVisibility}
              onColumnVisibilityModelChange={setTableColumnVisibility}
              columnWidths={columnWidths}
              onColumnWidthChange={handleColumnWidthChange}
              onColumnOrderChange={handleColumnOrderChange}
              sortModel={tableSortModel}
              onSortModelChange={setTableSortModel}
              density={density}
              loading={isLoading || isFeaturesLoading}
              isPanelOpen={isPanelOpen}
              onPanelOpen={openPanel}
              onPanelOpenAndFocus={openPanelAndFocus}
              totalResults={totalResults}
            />
          </Box>
        </Panels.Main>
        <Panels.Drawer>
          <ProjectDetails />
        </Panels.Drawer>
      </Panels>
    </>
  );
};
